import React, {
    useState,
    useEffect,
    ReactElement
} from "react";
import {
    Button,
    Input,
    Flex,
    Divider,
    Segmented,
    Tag
} from "antd";
import {
    UserOutlined,
    ClockCircleOutlined,
} from "@ant-design/icons";
import {
    useSearchParams
} from "react-router-dom";
import {
    useLazyQuery, useMutation
} from "@apollo/client";
import {
    RETRIEVE_BOOKINGS, RETRIEVE_BOOK_KPIS
} from "../../apollo/query/book.query";
import {
    ActionsContainer,
    AppointmentContainer,
    ContainerReservation
} from "../../styled-components/agenda";
import {
    OTPProps
} from "antd/es/input/OTP";
import {
    LOGIN_USER_RESTAURANT
} from "../../apollo/query/user.query";
import {
    ImageContainer
} from "../../styled-components/landing";
import {
    useTranslation
} from "react-i18next";
import dayjs from "dayjs";
import ReservationDetail from "../../components/reservation-detail";
import Navbar from "../../components/navbar";
import { UPDATE_STATUS_RESERVATION } from "../../apollo/mutation/book.mutation";
import Footer from "../../components/footer";

const Agenda = () => {
    /** Translation utilities */
    const{ t, i18n } = useTranslation();
    /** Navigation utilities */
    const [searchParams] = useSearchParams();
    /** Manage the appointment selected */
    const [selectedAppointment, setSelectedAppointment] = useState<any | null>(null);
    /** Retrieve the list of bookings */
    const [ retrieveBookings, { data } ] = useLazyQuery(RETRIEVE_BOOKINGS, { fetchPolicy: 'no-cache' })
    const [ retrieveBookKpi ] = useLazyQuery(RETRIEVE_BOOK_KPIS, { fetchPolicy: 'no-cache' })
    /** State to manage the reservations */
    const [reservations, setReservations] = useState<any>([]);
    /** State to manage the filter applied */
    const [filter, setFilter] = useState<string>('Todas');
    /** MAnage to login the user */
    const [ loginUserRestaurant ] = useLazyQuery(LOGIN_USER_RESTAURANT, { fetchPolicy: 'no-cache' });
    /** State to mantain which user is login */
    const [user, setUser] = useState<any>(null);
    /** Request to update the status of booking */
    const [ updateStatus ] = useMutation(UPDATE_STATUS_RESERVATION, { refetchQueries: ['gqlRetrieveBooksByRestaurant', 'gqlRetrieveKPIs'] });

    useEffect(() => {
        if( data?.data ) {
            const info = [...data.data].sort((a: any, b:any) => dayjs(a.dDate).isBefore(b.dDate) ? -1 : 1);
            const grouped = info?.reduce((acc: any, obj: any) => {
                const monthKey = dayjs(obj.dDate, { locale: i18n.language }).format("MMMM, YYYY");
            
                if (!acc[monthKey]) {
                    acc[monthKey] = [];
                }
            
                acc[monthKey].push(obj);
                return acc;
            }, {});

            setReservations(grouped);
      }
    }, [data, i18n.language]);
    
    const onChange: OTPProps['onChange'] = async (text) => {
        const { data } = await loginUserRestaurant({ variables: {
            nCode: text,
            nIDRestaurant: parseInt(searchParams.get('nIDRestaurant') ?? '-1')
        } });

        if( data?.data ) {
            setUser(data?.data);
            retrieveBookings({ variables: { nIDRestaurant: parseInt(searchParams.get('nIDRestaurant') ?? '-1') }});
            // format(new Date(), 'yyyy-MM-dd')
            retrieveBookKpi({ variables: { dDate: '2025-04-17', nIDRestaurant: parseInt(searchParams.get('nIDRestaurant') ?? '-1') }});
        }
    };

    const sharedProps: OTPProps = {
        onChange,
    };

    const LoginPage = () => (
        <Flex align="center" justify="center" style={{ height: '100%', width: '100%', backgroundColor: '#1F1F1F', paddingInline: 20 }}>
            <Navbar nIDRestaurant={parseInt(searchParams.get('nIDRestaurant') ?? '-1')} />
            <ImageContainer>
                    <img
                        src={`https://api.ams-carriot-restaurant.com/restaurant-image/${parseInt(searchParams.get('nIDRestaurant') ?? '-1')}`}
                        style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center' }}
                        alt="AMS Restaurant Interior"
                    />
                    <div style={{ position: 'absolute', inset: 0, backgroundColor: 'black', opacity: 0.5 }} />
                </ImageContainer>
            <Flex vertical gap={20} align="center" justify="center" style={{ width: 300, minHeight: 300, padding: 20, backgroundColor: '#282828', borderRadius: 10, zIndex: 3 }}>
                <h2 style={{ fontFamily: 'Suburban', color: 'white',  fontSize: '3rem', margin: 0 }}>
                    { t('agenda.title') }
                </h2>
                <p style={{ fontFamily: 'Roboto-Thin', color: 'white', fontSize: '0.9rem' }}>
                    { t('agenda.description') }
                </p>
                <Input.OTP mask="🔒" length={4} {...sharedProps} style={{ width: '100%' }} />
            </Flex>
        </Flex>
    );

    const renderActionsAppointment = (appointment: any): ReactElement => (
        <>
            {/* eslint-disable-next-line eqeqeq */}
            { appointment.nStatus == 0 && 
                <Button type="default" onClick={() => window.open(`https://web.whatsapp.com/send?phone=${appointment.sPhone}`) }>
                    { t('buttons.send-message') }
                </Button>
            }
            {/* eslint-disable-next-line eqeqeq */}
            { appointment.nStatus == 0 && 
                <Button type="default" onClick={() => updateStatus({ variables: { nID: appointment.nID, nIDRestaurant: parseInt(searchParams.get('nIDRestaurant') ?? '-1'), nIDUser: user?.nID, nStatus: 1 } })}>
                    { t('buttons.served') }
                </Button>
            }
            {/* eslint-disable-next-line eqeqeq */}
            { appointment.nStatus == 0 &&
                <Button type="default" onClick={() => setSelectedAppointment(appointment)}>
                    { t('buttons.edit') }
                </Button>
            }
            {/* eslint-disable-next-line eqeqeq */}
            { appointment.nStatus == 1 && <Tag color='success'> { t('buttons.served') } </Tag> }
            {/* eslint-disable-next-line eqeqeq */}
            { appointment.nStatus == 2 && <Tag color='error'> { t('agenda.cancelled') } </Tag> }
        </>
    );

    const AgendaPage = () => (
        <div style={{ height: '100%', width: '100%', backgroundColor: '#1F1F1F', overflowY: 'hidden' }}>
            <Navbar login setUser={setUser} nIDRestaurant={parseInt(searchParams.get('nIDRestaurant') ?? '-1')} />
            <Divider />
            <Flex gap={30} justify="center" wrap='wrap' style={{ paddingInline: 30, paddingTop: 20 }}>
                <Flex vertical align="center" gap={5} style={{ backgroundColor: 'rgb(55, 55, 55)', padding: 10, paddingInline: 40, borderRadius: 20 }}>
                    <span style={{ fontFamily: 'SubUrban', color: 'white', fontSize: '1.5rem', letterSpacing: 1 }}> Total </span>
                    <span style={{ fontFamily: 'Roboto-Bold', color: 'white', fontSize: '1.5rem' }}> 8 </span>
                </Flex>
                <Flex vertical align="center" gap={5} style={{ backgroundColor: 'rgb(55, 55, 55)', padding: 10, paddingInline: 40, borderRadius: 20 }}>
                    <span style={{ fontFamily: 'SubUrban', color: 'white', fontSize: '1.5rem', letterSpacing: 1 }}> Pendientes </span>
                    <span style={{ fontFamily: 'Roboto-Bold', color: 'white', fontSize: '1.5rem' }}> 2 </span>
                </Flex>
                <Flex vertical align="center" gap={5} style={{ backgroundColor: 'rgb(55, 55, 55)', padding: 10, paddingInline: 40, borderRadius: 20 }}>
                    <span style={{ fontFamily: 'SubUrban', color: 'white', fontSize: '1.5rem', letterSpacing: 1 }}> Servidas </span>
                    <span style={{ fontFamily: 'Roboto-Bold', color: 'white', fontSize: '1.5rem' }}> 6 </span>
                </Flex>
            </Flex>
            <Flex style={{ paddingInline: 30, paddingTop: 20 }}>
                <Segmented<string>
                    className='agenda-segmented'
                    style={{ background: 'transparent', color: 'white', display: 'flex', flexWrap: 'wrap' }}
                    value={filter}
                    options={['Todas', 'Pendientes', 'Servidas', 'Canceladas']}
                    onChange={(value: any) => {
                        setFilter(value);
                    }}
                />
            </Flex>
            <div style={{ paddingTop: 20 }}>
                <Flex vertical align="center" gap={50} style={{ height: '100%', width: '100%', paddingBottom: 30 }}>
                    { Object.entries(reservations).map(([key, values]: any) => (
                         <Flex key={key} vertical gap={10} align="center" justify="space-between" style={{ width: '100%' }}>
                            <span style={{ textAlign: 'left', width: '90%', fontFamily: 'Roboto-Bold', color: 'white', textTransform: 'capitalize' }}> { key } </span>
                            {/* eslint-disable-next-line eqeqeq */}
                            { values?.filter((x: any) => filter === 'Todas' ? true : filter === 'Pendientes' ? x.nStatus == 0 : filter === 'Canceladas' ? x.nStatus == 2 : x.nStatus == 1 )?.map((appointment: any) => (
                                <ContainerReservation gap={10} align="center" justify="space-between"
                                    key={appointment.nID}
                                    style={{
                                        // eslint-disable-next-line eqeqeq
                                        backgroundColor: appointment.nStatus == 1 ? '#a8ffc0' : appointment.nStatus == 2 ? '#FCA5A5' : 'white',
                                        borderRadius: 10,
                                        width: '90%',
                                        padding: '10px',
                                        cursor: 'pointer',
                                        // eslint-disable-next-line eqeqeq
                                        border: appointment.nStatus == 1 ? '1px solid #10B981' : appointment.nStatus == 2 ? '1px solid #B91C1C' : '1px solid #E5E7EB',
                                        minHeight: 50
                                    }}
                                >
                                    <AppointmentContainer>
                                        <Flex vertical justify="center" align="center">
                                            <span style={{ fontFamily: 'Roboto-Bold', fontSize: '0.8rem', textTransform: 'capitalize' }}>
                                                { new Date(dayjs(appointment.dDate).format("YYYY-MM-DD")).toLocaleString('es-ES', { weekday:'long' }) }
                                            </span>
                                            <span style={{ fontFamily: 'Roboto-Bold', fontSize: '2rem' }}>
                                                { dayjs(appointment.dDate).format("DD") }
                                            </span>
                                        </Flex>
                                        <Divider className="appointment-divider" type="vertical" style={{ margin: 2, border: 'none', borderLeft: '1px solid #E5E7EB', height: 50 }} />
                                        <Flex vertical justify='center' gap={10} style={{ width: '100%' }}>
                                            <Flex gap={10} align="center">
                                                <ClockCircleOutlined style={{ fontSize: 12, color: '#404040' }} />
                                                <span style={{ fontFamily: 'Roboto-Thin', fontSize: '.8rem' }}> { appointment.sTime } </span>
                                            </Flex>
                                            <Flex gap={10} align="center">
                                                <UserOutlined style={{ fontSize: 12, color: '#404040' }} />
                                                <span style={{ fontFamily: 'Roboto-Thin', fontSize: '.8rem' }}> { appointment.nGuests } { t('label.customers') } </span>
                                            </Flex>
                                        </Flex>
                                        <Flex vertical gap={10} style={{ width: '100%', marginTop: 10 }}>
                                            <span style={{ fontFamily: 'Roboto-Bold', fontSize: '1.2rem' }}> { appointment.sName } </span>
                                            <span style={{ fontFamily: 'Roboto-Thin', fontSize: '0.9rem' }}> { appointment.sDescription } </span>
                                        </Flex>
                                        <Flex className="inside-actions" gap={20} wrap='wrap' style={{ width: '100%', marginTop: 20 }}>
                                            { renderActionsAppointment(appointment) }
                                        </Flex>
                                    </AppointmentContainer>
                                    <ActionsContainer gap={20} wrap='wrap'>
                                        { renderActionsAppointment(appointment) }
                                    </ActionsContainer>
                                </ContainerReservation>
                            ))}
                         </Flex>
                    ))}
                    
                </Flex>
            </div>
            { selectedAppointment &&
                <ReservationDetail
                    appointment={selectedAppointment}
                    setSelectedAppointment={setSelectedAppointment}
                    nIDRestaurant={parseInt(searchParams.get('nIDRestaurant') ?? '-1')}
                    nIDUser={user?.nID}
                />
            }
        </div>
    );

    return (
        <Flex vertical style={{ width: '100%', height: '100%' }}> 
            <div style={{ height: '100%' }}>
                { user ? <AgendaPage /> : <LoginPage /> }
            </div>
            <Footer nIDRestaurant={parseInt(searchParams.get('nIDRestaurant') ?? '-1')} />
        </Flex>
    );
};

export default Agenda;
